import { config } from '../../config';
import { NetworkActions } from '../network';
import { NetworkService } from '../../services';
import { SuiteConstants } from './types';
import { UtilityActions } from '../utility';

// API endpoints
const { BASE_URL } = config;
const THERMOSTATS = `${BASE_URL}/thermostats/api/thermostats/`;

export class SuiteActions {
  static loadSuiteGraphPage({ buildingSlug, subcategory, uid, dataPoints = [], graphDateRange = {} }) {
    return async (dispatch) => {
      dispatch({ type: SuiteConstants.StartLoading });
      await dispatch(SuiteActions.getSuiteDataPoints(buildingSlug, uid, subcategory));
      await dispatch(SuiteActions.getSuiteDetails(buildingSlug, uid, subcategory));
      await dispatch(SuiteActions.getSuiteData(buildingSlug, uid, subcategory, dataPoints, graphDateRange));

      window.sessionStorage.setItem('thermostatUid', JSON.stringify(uid));

      dispatch({ type: SuiteConstants.FinishLoading });
    };
  }

  static getSuiteList(buildingSlug, subcategory, thermostatUid) {
    return async (dispatch) => {
      const requestInfo = {
        category: 'thermostats',
        subcategory,
      };
      await dispatch(NetworkActions.updateLastRequest(requestInfo));

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');

        const urlFormatted = `${THERMOSTATS}${buildingSlug}/list/`;
        return fetch(urlFormatted, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);

      if (!response.ok) {
        return dispatch({
          type: SuiteConstants.SuiteError,
          payload: 'There was an error receiving suite data.',
        });
      }

      const data = await response.json();

      if (data.length > 0) {
        await dispatch(SuiteActions.formatSuiteList(data, thermostatUid));
      }
      return dispatch({ type: SuiteConstants.ReceiveSuiteList, payload: data });
    };
  }

  static formatSuiteList(suiteList, selectedSuiteUid) {
    return async (dispatch) => {
      const formattedList = [];
      let selectedSuite = undefined;
      let sessionStorageUid;
      if (!selectedSuiteUid) {
        sessionStorageUid = JSON.parse(window.sessionStorage.getItem('thermostatUid'));
      }
      suiteList.forEach((currentValue, index) => {
        formattedList[index] = {
          value: currentValue.uid_hash,
          label: currentValue.display_name,
        };
        if (currentValue.uid_hash === selectedSuiteUid || currentValue.uid_hash === sessionStorageUid) {
          selectedSuite = formattedList[index];
        }
      });

      if (!selectedSuite) {
        const [firstSuite = {}] = formattedList;
        selectedSuite = firstSuite;
      }

      await dispatch(SuiteActions.setSelectedSuite(selectedSuite));
      return dispatch({ type: SuiteConstants.FormattedSuiteList, payload: formattedList });
    };
  }

  static getSuiteData(buildingSlug, uid, subcategory = undefined, dataPointList = [], graphDateRange) {
    return async (dispatch) => {
      const requestInfo = {
        category: 'thermostats',
        subcategory,
      };
      await dispatch(NetworkActions.updateLastRequest(requestInfo));

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        let urlFormatted = `${THERMOSTATS}${buildingSlug}/${uid}/data/?`;
        if (dataPointList.length > 0) {
          urlFormatted = `${urlFormatted}points=${dataPointList.join(',')}`;
        }
        if (graphDateRange) {
          const options = UtilityActions.getUtilityOptions(graphDateRange, 'Thermostat');
          urlFormatted = UtilityActions.formatUtilityUrl(urlFormatted, options);
        }

        return fetch(urlFormatted, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok) {
        return dispatch({
          type: SuiteConstants.SuiteError,
          payload: 'There was an error receiving suite data.',
        });
      }

      const data = await response.json();
      const suiteLineColors = JSON.parse(window.sessionStorage.getItem('suiteLineColors'));
      const availableSuiteLineColors = JSON.parse(window.sessionStorage.getItem('availableSuiteLineColors'));
      let isSessionStorageCorrect;

      if (suiteLineColors) {
        isSessionStorageCorrect = SuiteActions.areEqual(dataPointList, Object.keys(suiteLineColors));
      }

      if (isSessionStorageCorrect && suiteLineColors && availableSuiteLineColors) {
        // used when refreshing the page to repopulate the reducer
        await dispatch(SuiteActions.updateLineColors(suiteLineColors, availableSuiteLineColors));
      } else {
        await dispatch(SuiteActions.setLineColorsObj(data.point_data));
      }

      await dispatch(SuiteActions.updateSelectedPointsList());
      return dispatch({ type: SuiteConstants.ReceiveSuiteData, payload: data });
    };
  }

  static areEqual(array1 = [], array2 = []) {
    // compare two arrays as sorted strings
    if (array1.sort().join(',') === array2.sort().join(',')) {
      return true;
    }
    return false;
  }

  static getSuiteDataPoints(buildingSlug, uid, subcategory = undefined) {
    return async (dispatch) => {
      const requestInfo = {
        category: 'thermostats',
        subcategory,
      };
      await dispatch(NetworkActions.updateLastRequest(requestInfo));

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');

        const urlFormatted = `${THERMOSTATS}${buildingSlug}/${uid}/data-points/`;
        return fetch(urlFormatted, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok) {
        return dispatch({
          type: SuiteConstants.SuiteError,
          payload: 'There was an error receiving suite data.',
        });
      }

      const data = await response.json();
      return dispatch({ type: SuiteConstants.ReceiveSuiteDataPoints, payload: data });
    };
  }

  static getSuiteDetails(buildingSlug, uid, subcategory = undefined) {
    return async (dispatch) => {
      dispatch({ type: SuiteConstants.RequestSuiteDetails });
      const requestInfo = {
        category: 'thermostats',
        subcategory,
      };
      await dispatch(NetworkActions.updateLastRequest(requestInfo));
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');

        const urlFormatted = `${THERMOSTATS}${buildingSlug}/${uid}/`;
        return fetch(urlFormatted, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok) {
        return dispatch({
          type: SuiteConstants.SuiteError,
          payload: 'There was an error receiving suite data.',
        });
      }

      const data = await response.json();
      return dispatch({ type: SuiteConstants.ReceiveSuiteDetails, payload: data });
    };
  }

  static setSelectedSuite(suite) {
    return { type: SuiteConstants.SetSelectedSuite, payload: suite };
  }

  static clearErrors() {
    return { type: SuiteConstants.ClearError };
  }

  static updateSelectedPointsList() {
    return (dispatch, getState) => {
      const suiteLineColors = JSON.parse(window.sessionStorage.getItem('suiteLineColors'));
      let selectedPointIds = [];

      try {
        selectedPointIds = Object.keys(suiteLineColors);
      } catch (e) {
        return null;
      }

      const state = getState();
      const { allSuiteDataPoints = [] } = state.suiteReducer;

      const selectedPointsList = allSuiteDataPoints.filter((point) => selectedPointIds.includes(point.id));
      return dispatch({ type: SuiteConstants.UpdateSelectedPointsList, payload: selectedPointsList });
    };
  }

  // On initial load this function sets:
  // - the selected registers list
  // - the id's mapped to colors
  // - the available colours
  static setLineColorsObj(selectedDataPoints) {
    const availableSuiteLineColors = ['line0', 'line1', 'line2', 'line3', 'line4', 'line5', 'line6'];
    const tmpColorObj = {};

    selectedDataPoints.map((dataPoint) => {
      tmpColorObj[dataPoint.id] = availableSuiteLineColors.shift();
      return tmpColorObj;
    });

    window.sessionStorage.setItem('suiteLineColors', JSON.stringify(tmpColorObj));
    window.sessionStorage.setItem('availableSuiteLineColors', JSON.stringify(availableSuiteLineColors));

    return (dispatch) => {
      dispatch({ type: SuiteConstants.UpdateLineColorsObj, payload: tmpColorObj });
      return dispatch({ type: SuiteConstants.UpdateAvailableColors, payload: availableSuiteLineColors });
    };
  }

  static updateLineColors(suiteLineColors, availableSuiteLineColors) {
    window.sessionStorage.setItem('suiteLineColors', JSON.stringify(suiteLineColors));
    window.sessionStorage.setItem('availableSuiteLineColors', JSON.stringify(availableSuiteLineColors));

    return (dispatch) => {
      dispatch({ type: SuiteConstants.UpdateLineColorsObj, payload: suiteLineColors });
      return dispatch({ type: SuiteConstants.UpdateAvailableColors, payload: availableSuiteLineColors });
    };
  }

  static clearSuiteData() {
    return { type: SuiteConstants.ClearSuiteData };
  }

  static setSelectedBuilding(buildingSlug) {
    return { type: SuiteConstants.SetSelectedBuilding, payload: buildingSlug };
  }

  static updateGraphDateRange(graphDateRange) {
    window.sessionStorage.setItem('thermostatGraphDateRange', JSON.stringify(graphDateRange));
    return { type: SuiteConstants.UpdateGraphDateRange, payload: graphDateRange };
  }
}
